import { Box, SxProps, Theme } from "@mui/material";

const SUIT_SYMBOLS: Record<string, string> = {
  S: "♠",
  H: "♥",
  D: "♦",
  C: "♣",
};

/** Single-letter suit code from `toCardCode` / cardsJS */
export function isRedSuitCode(suitCode: string): boolean {
  return suitCode === "H" || suitCode === "D";
}

type Props = {
  rankCode: string;
  suitCode: string;
  back?: boolean;
  /** Approx width in px; height scales ~1.45× */
  widthPx?: number;
  sx?: SxProps<Theme>;
};

/**
 * Styled playing card (face + back) matching the demo card look — no external SVG fetch.
 */
export function PlayingCardFace({ rankCode, suitCode, back = false, widthPx = 56, sx }: Props) {
  const h = Math.round(widthPx * 1.43);
  const sym = SUIT_SYMBOLS[suitCode] ?? suitCode;
  const isRed = isRedSuitCode(suitCode);

  if (back) {
    return (
      <Box
        sx={{
          width: widthPx,
          height: h,
          borderRadius: 2,
          border: "1px solid rgba(148, 163, 184, 0.45)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
          background: "linear-gradient(135deg, #fb7185 0%, #dc2626 45%, #fb923c 100%)",
          position: "relative",
          overflow: "hidden",
          flexShrink: 0,
          ...sx,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 6,
            borderRadius: 1.5,
            border: "1px solid rgba(255,255,255,0.35)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.22,
            backgroundImage: "radial-gradient(circle at 10px 10px, rgba(255,255,255,0.9) 2px, transparent 2.5px)",
            backgroundSize: "20px 20px",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: Math.min(36, widthPx * 0.55),
              height: Math.min(36, widthPx * 0.55),
              borderRadius: "50%",
              border: "3px solid rgba(255,255,255,0.65)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.95)",
              fontWeight: 900,
              fontSize: Math.max(14, widthPx * 0.28),
            }}
          >
            A
          </Box>
        </Box>
      </Box>
    );
  }

  const cornerSize = Math.max(11, widthPx * 0.2);
  const centerSize = Math.max(22, widthPx * 0.42);

  return (
    <Box
      sx={{
        width: widthPx,
        height: h,
        borderRadius: 2,
        bgcolor: "#fff",
        border: "1px solid rgba(148, 163, 184, 0.55)",
        boxShadow: "0 8px 22px rgba(0,0,0,0.28)",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 6,
          left: 6,
          fontWeight: 800,
          lineHeight: 1,
          color: isRed ? "#ef4444" : "#0f172a",
          fontSize: cornerSize,
        }}
      >
        <Box component="span" sx={{ display: "block" }}>
          {rankCode}
        </Box>
        <Box component="span" sx={{ display: "block", fontSize: cornerSize * 0.92 }}>
          {sym}
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 600,
          color: isRed ? "#ef4444" : "#0f172a",
          fontSize: centerSize,
        }}
      >
        {sym}
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: 6,
          right: 6,
          fontWeight: 800,
          lineHeight: 1,
          color: isRed ? "#ef4444" : "#0f172a",
          fontSize: cornerSize,
          transform: "rotate(180deg)",
        }}
      >
        <Box component="span" sx={{ display: "block" }}>
          {rankCode}
        </Box>
        <Box component="span" sx={{ display: "block", fontSize: cornerSize * 0.92 }}>
          {sym}
        </Box>
      </Box>
    </Box>
  );
}
